<template>
<div class='h-pos-rel'
     :class='wrapperClass'>
    <transition name='animate-opacity-global'>
        <div v-show='isPageLoaded && arrowKey'
             :class='[`${newClass} ${newClass}--right ${newClassModifier}`, isScrollToRightExists]'
             :style='shadowStyles'
             @click='scrollToRight'>
            <FwIcon
                :class='[
                    iconClass,
                    { "h-ml-30": isBaseArrow }
                ]'
                class='fw-horizontal-scroll__icon'
                :icon='isBaseArrow ? "circle-arrow-right" : "arrow-right"'
                :size='sizeIcon'
                :color='colorIcon'>
            </FwIcon>
        </div>
    </transition>

    <transition name='animate-opacity-global'>
        <div v-show='isPageLoaded && arrowKey'
             :class='[`${newClass} ${newClass}--left ${newClassModifier}`, isScrollToLeftExists]'
             :style='shadowStyles'
             @click='scrollToLeft'>
            <FwIcon
                :class='[
                    iconClass,
                    { "h-mr-30": isBaseArrow }
                ]'
                class='fw-horizontal-scroll__icon'
                :icon='isBaseArrow ? "circle-arrow-left" : "arrow-left"'
                :size='sizeIcon'
                :color='colorIcon'>
            </FwIcon>
        </div>
    </transition>

    <div ref='contentWrapper'
         class='fw-horizontal-scroll__content'
         :class='slotScrollClass'>
        <div class='fw-horizontal-scroll__inner'
             :class='slotWrapperClass'>
            <slot name='content'></slot>
        </div>
    </div>
</div>
</template>

<script lang='ts'>
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

@Component
export default class FwHorizontalScroll extends Vue {
    @Prop({ type: Boolean }) readonly isPageLoaded!: boolean;
    @Prop({ type: Boolean }) readonly hideBtn!: boolean;
    @Prop({ type: Number, default: 100 }) readonly arrowBgWidth!: number;
    @Prop({ type: String, default: null }) readonly btnLocation!: string;
    @Prop({ type: String, default: 'fw-horizontal-scroll__shadow' }) readonly newClass!: string;
    @Prop({ type: String, default: '' }) readonly newClassModifier!: string;
    @Prop({ type: String, default: null }) readonly colorIcon!: string;
    @Prop({ type: String, default: null }) readonly sizeIcon!: string;
    @Prop({ type: String, default: null }) readonly slotWrapperClass!: string;
    @Prop({ type: String, default: null }) readonly slotScrollClass!: string;
    @Prop({ type: String, default: null }) readonly wrapperClass!: string;
    @Prop({ type: String, default: null }) readonly iconClass!: string;
    @Prop({ type: Number, default: 1 }) readonly arrowKey!: number;

    scrollWidth: number = 0;
    wrapperWidth: number = 0;
    reloadKey: number = 1;
    scrollLeft: number = 0;

    get shadowStyles() {
        return {
            width: `${this.arrowBgWidth}px`,
        };
    }

    get isBaseArrow() {
        return ['fw-horizontal-scroll__shadow', 'fw-horizontal-arrow__right'].includes(this.newClass);
    }

    get isScrollToRightExists() {
        if (this.reloadKey) {
            if (this.btnLocation === 'between' && this.scrollWidth <= this.wrapperWidth + this.scrollLeft) {
                return 'hideBtn';
            } else if (this.btnLocation === 'right' && this.scrollWidth <= this.wrapperWidth + this.scrollLeft) {
                return 'disabledBtn';
            }
        }
    }

    get isScrollToLeftExists() {
        if (this.reloadKey) {
            if (this.btnLocation === 'between' && this.scrollLeft <= 0 && this.scrollLeft <= this.scrollWidth - this.wrapperWidth) {
                return 'hideBtn';
            } else if (this.btnLocation === 'right' && this.scrollLeft <= 0 && this.scrollLeft <= this.scrollWidth - this.wrapperWidth) {
                return 'disabledBtn';
            }
        }
    }

    calculateScrollState() {
        const wrapper: HTMLElement | null = (this.$refs.contentWrapper as HTMLElement);
        if (!wrapper) return;
        this.scrollWidth = wrapper.scrollWidth;
        this.wrapperWidth = wrapper.offsetWidth;
        this.scrollLeft = wrapper.scrollLeft;
    }

    scrollToRight() {
        const wrapper: HTMLElement = (this.$refs.contentWrapper as HTMLElement);
        wrapper.scrollTo({
            left: this.scrollLeft + this.wrapperWidth,
            behavior: 'smooth',
        });
    }

    scrollToLeft() {
        const wrapper: HTMLElement = (this.$refs.contentWrapper as HTMLElement);
        wrapper.scrollTo({
            left: this.scrollLeft - this.wrapperWidth,
            behavior: 'smooth',
        });
    }

    async mounted() {
        (this.$refs.contentWrapper as HTMLElement).addEventListener('scroll', this.calculateScrollState);
        await this.$nextTick();
        setTimeout(() => {
            this.calculateScrollState();
        }, 1);
    }

    @Watch('arrowKey')
    async arrowKeyHandler() {
        await this.$nextTick();
        this.calculateScrollState();
        this.reloadKey += 1;
    }
}
</script>

<style lang='sass'>
.hideBtn
    display: none!important
.disabledBtn
    -webkit-filter: grayscale(100%)
    filter: grayscale(100%)
    transition: 1s
.fw-horizontal-scroll__content
    width: 100%
    overflow-x: auto
    -ms-overflow-style: none
    scrollbar-width: none
    scrollbar-height: none
    position: relative

    &::-webkit-scrollbar-thumb
        background: transparent

    &::-webkit-scrollbar
        display: none
        width: 0 !important
        height: 0 !important
        -webkit-appearance: none
        background: transparent

.fw-horizontal-scroll__shadow
    position: absolute
    top: 0
    height: 100%
    display: flex
    align-items: center
    z-index: 5

    &--left
        left: 0
        justify-content: flex-end
        background: linear-gradient(to left, rgba(242,244,246,0) 0%,rgba(242,244,246,0.46) 27%,rgba(242,244,246,0.6) 35%,rgba(242,244,246,1) 100%)

    &--right
        right: 0
        justify-content: flex-start
        background: linear-gradient(to right, rgba(242,244,246,0) 0%,rgba(242,244,246,0.46) 27%,rgba(242,244,246,0.6) 35%,rgba(242,244,246,1) 100%)
.fw-horizontal-arrow__right
    position: absolute
    height: 100%
    top: 50%
    display: flex
    align-items: center
    z-index: 5
    &--left
        height: 40px
        right: 0
        justify-content: center
        .fw-horizontal-scroll__icon
            margin-right: 0
    &--right
        height: 40px
        right: 0
        margin-top: -35px
        justify-content: center
        .fw-horizontal-scroll__icon
            margin-left: 0
.fw-horizontal-scroll__inner
    min-height: 250px
    display: flex
    align-items: flex-start

.fw-horizontal-scroll__icon
    cursor: pointer
    opacity: 0.8
    transition: opacity .2s

    &:hover
        opacity: 1

$big-circle-size: 45px
$big-circle-middle-size: 24px

.fw-arrow-big-circle
    position: absolute

.fw-arrow-big-circle-fixed
    position: fixed

.fw-arrow-big-circle, .fw-arrow-big-circle-fixed
    top: 0
    bottom: 0
    margin: auto
    z-index: 5
    width: $big-circle-size
    height: $big-circle-size * 2
    display: flex
    align-items: center
    justify-content: center
    cursor: pointer

    &:hover
        background-color: $main-green !important

    .fw-horizontal-scroll__icon
        display: flex

    &--right
        right: 0
        border-radius: 45px 0 0 45px
        background-color: $main-gray
        .fw-horizontal-scroll__icon
            position: relative
            left: 4px

    &--left
        left: 0
        border-radius: 0 45px 45px 0
        background-color: $main-gray
        .fw-horizontal-scroll__icon
            position: relative
            right: 4px

.fw-arrow-big-circle--middle-size
    width: $big-circle-middle-size
    height: $big-circle-middle-size * 2
</style>
